"use client"

import { useEffect, useRef, useState } from "react"
import { Divider, Box, SkeletonCircle, SkeletonText } from "@chakra-ui/react"
import { Navigation, Pagination, Scrollbar, A11y, Autoplay } from "swiper/modules"
import { Swiper, SwiperSlide } from "swiper/react"

// Import Swiper styles
import "swiper/css"
import "swiper/css/navigation"
import "swiper/css/pagination"
import "swiper/css/scrollbar"
import "./JobRoles.css"
import { useDispatch, useSelector } from "react-redux"
import { fetchJobRolesAsync, selectJobRoles } from "./JobRolesSlice"
import { Link } from "react-router-dom"
import { faBriefcase } from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import companylogo from "./logo1png.png"
import { NoDataFound } from "../../pages/CommonPublicPages/Basic/NoDataFound"

function JobRoles() {
  const dispatch = useDispatch()
  const jobs = useSelector(selectJobRoles)
  const base_url = "https://api.indiahirings.com/uploads/company/"
  const swiperRef = useRef(null)
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    setIsLoading(true)
    dispatch(fetchJobRolesAsync())
      .then(() => {
        setIsLoading(false)
      })
      .catch(() => {
        setIsLoading(false)
      })
  }, [dispatch])

  const handleMouseEnter = () => {
    if (swiperRef.current && swiperRef.current.autoplay) {
      swiperRef.current.autoplay.stop()
    }
  }

  const handleMouseLeave = () => {
    if (swiperRef.current && swiperRef.current.autoplay) {
      swiperRef.current.autoplay.start()
    }
  }

  // Function to render job content based on loading and data state
  const renderJobContent = () => {
    if (isLoading) {
      // Show skeleton loader when data is being fetched
      return (
        <>
          {Array.from({ length: 3 }).map((_, index) => (
            <SwiperSlide key={index}>
              <Box width="full" padding="6" boxShadow="sm" bg="white" borderRadius="xl">
                <div className="flex items-start space-x-4">
                  <SkeletonCircle size="12" />
                  <div className="flex-1">
                    <SkeletonText mt="2" noOfLines={2} spacing="2" skeletonHeight="3" />
                  </div>
                </div>
                <Divider my="4" />
                <SkeletonText mt="2" noOfLines={1} spacing="2" skeletonHeight="8" />
              </Box>
            </SwiperSlide>
          ))}
        </>
      )
    } else if (!Array.isArray(jobs) || jobs.length === 0) {
      // Show NoDataFound component when there's no data
      return (
        <div className="w-full ">
          <NoDataFound
            title="No Job Roles Found"
            message="We couldn't find any job roles at the moment. Please check back later."
          />
        </div>
      )
    } else {
      // Show actual job data
      return jobs.map((job, index) => (
        <SwiperSlide key={index}>
          <Link key={job.uuid} to={`/jobs-roles/${job.uuid}`}>
            <div
              className="w-full group bg-white hover:shadow-lg border-black-100 overflow-hidden inset-0 border-2 rounded-xl transition-all duration-300 hover:border-blue-500 hover:animate-border"
              onMouseEnter={handleMouseEnter}
              onMouseLeave={handleMouseLeave}
            >
              <div className="shadow-md p-4 md:p-6 rounded-lg bg-white hover:bg-mainBg">
                <div className="flex items-start space-x-3 md:space-x-4">
                  <div className={`flex-shrink-0 w-12 h-12 rounded-lg bg-blue-100 flex items-center justify-center`}>
                    <FontAwesomeIcon icon={faBriefcase} className={`text-blue-500 text-2xl`} />
                  </div>

                  <div spacing={1} className="flex-1 min-w-0">
                    <div className="text-lg md:text-xl font-bold text-gray-800 truncate group-hover:text-blue-600 transition-colors">
                      {job.title}
                    </div>
                    <div className="text-sm text-blue-600 font-medium">{job.job_count}+ Jobs available</div>
                  </div>
                </div>
                <Divider className="my-3 md:my-4" />
                <div marginBottom={0}>
                  <div className="flex items-center gap-2 md:gap-4">
                    {Array.isArray(job.job_images) && job.job_images.length > 0 ? (
                      job.job_images.map((image, index) => (
                        <img
                          key={index}
                          src={image ? `${base_url}${image}` : companylogo}
                          alt={`Job Image}`}
                          className="w-10 h-10 md:w-12 md:h-12 object-contain rounded-full"
                          onError={(e) => (e.target.src = companylogo)}
                        />
                      ))
                    ) : (
                      <img
                        src="https://images.rawpixel.com/image_png_800/cHJpdmF0ZS9sci9pbWFnZXMvd2Vic2l0ZS8yMDIyLTA5L2pvYjExMy1lbGVtZW50LTAwMS1wLnBuZw.png"
                        alt="Default"
                        className="w-10 h-10 md:w-12 md:h-12 rounded-full"
                      />
                    )}
                  </div>
                </div>
              </div>
            </div>
          </Link>
        </SwiperSlide>
      ))
    }
  }

  return (
    <div className="w-full px-4 md:px-14">
      <div
        className="px-4 md:px-10 py-8 rounded-xl sm:block job-roles-container myjobs"
        style={{
          background:
            "linear-gradient(340deg, rgba(119, 161, 211, 0.2),rgba(121, 203, 202,0.1),rgba(254, 234, 234, 1))",
        }}
      >
        <div className="py-6 md:py-10">
          <div className="text-center">
            <h3 className="text-3xl mb-2 md:text-4xl font-semibold text-gray-900">
              Popular <span className="text-blue-500">Job Roles</span>
            </h3>
            <p className="text-gray-600 text-sm md:text-base px-2">
              Explore the most sought-after positions in the industry
            </p>
          </div>
        </div>

        <div className="w-full">
          <Swiper
            className="px-3 md:p-[40px]"
            modules={[Navigation, Pagination, Scrollbar, A11y, Autoplay]}
            spaceBetween={20}
            slidesPerView={1}
            breakpoints={{
              640: {
                slidesPerView: 2,
                spaceBetween: 30,
              },
              1024: {
                slidesPerView: 3,
                spaceBetween: 40,
              },
            }}
            navigation={{
              nextEl: ".swiper-button-next",
              prevEl: ".swiper-button-prev",
            }}
            autoplay={{ delay: 3000, disableOnInteraction: false }}
            onSwiper={(swiper) => {
              swiperRef.current = swiper
            }}
          >
            {renderJobContent()}
          </Swiper>
        </div>
      </div>
    </div>
  )
}

export default JobRoles